<script setup>
import { onMounted, onUnmounted, watch, defineProps, ref } from 'vue';

const props = defineProps({
  loadMore: {
    type: Function,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  hasMore: {
    type: Boolean,
    default: true
  },
  threshold: {
    type: Number,
    default: 300
  },
  enabled: {
    type: Boolean,
    default: true
  },
  hasItems: {
    type: Boolean,
    default: false
  },
  scrollContainer: {
    type: String,
    default: null
  }
});

const containerElement = ref(null);

const checkScroll = () => {
  if (!props.enabled || props.loading || !props.hasMore) return;
  
  const container = props.scrollContainer 
    ? document.querySelector(props.scrollContainer)
    : window;
    
  const scrollHeight = container === window 
    ? document.documentElement.scrollHeight
    : container.scrollHeight;
    
  const scrollTop = container === window
    ? window.scrollY
    : container.scrollTop;
    
  const clientHeight = container === window
    ? window.innerHeight
    : container.clientHeight;
    
  if (scrollHeight - (scrollTop + clientHeight) < props.threshold) {
    props.loadMore();
  }
};

onMounted(() => {
  const container = props.scrollContainer 
    ? document.querySelector(props.scrollContainer)
    : window;
    
  containerElement.value = container;
  container.addEventListener('scroll', checkScroll);
  
});

onUnmounted(() => {
  if (containerElement.value) {
    containerElement.value.removeEventListener('scroll', checkScroll);
  }
});

watch(() => props.hasMore, (newValue) => {
  if (newValue && !props.loading && props.hasItems) {
    setTimeout(() => checkScroll(), 100);
  }
});

watch(() => props.enabled, (newValue) => {
  if (newValue && !props.loading && props.hasMore && props.hasItems) {
    setTimeout(() => checkScroll(), 100);
  }
});
</script>

<template>
  <div class="infinite-scroll">
    <slot></slot>
    
    
    <!-- Loading indicator -->
    <div v-if="loading" class="infinite-scroll__loading">
      <slot name="loading">
        <div class="infinite-scroll__spinner"></div>
      </slot>
    </div>
  </div>
</template>

<style scoped>
.infinite-scroll {
  width: 100%;
}

.infinite-scroll__loading,
.infinite-scroll__end,
.infinite-scroll__button-container {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
}

.infinite-scroll__spinner {
  width: 2rem;
  height: 2rem;
  border: 0.25rem solid rgba(156, 163, 175, 0.3);
  border-top-color: #FE3E01;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.infinite-scroll__end-text {
  color: #6b7280;
  font-size: 0.875rem;
}

.infinite-scroll__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 1rem;
  background-color: #FE3E01;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 0.375rem;
  transition: background-color 0.2s;
}

.infinite-scroll__button:hover {
  background-color: #FE3E01;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-color-scheme: dark) {
  .infinite-scroll__end-text {
    color: #9ca3af;
  }
  
  .infinite-scroll__spinner {
    border-color: rgba(75, 85, 99, 0.3);
    border-top-color: #FE3E01;
  }
  
  .infinite-scroll__button {
    background-color: #FE3E01;
  }
  
  .infinite-scroll__button:hover {
    background-color: #FE3E01;
  }
}
</style>
